/**
 * What staff did to your account that you must see (QA 2026-10-28):
 *
 * - a warning (CO-BUG-13): shown at once (`NX_WARNING_CREATE`) and at every
 *   start until you press "I understand"; it names the category and what
 *   staff wrote to you, never who or why internally;
 * - a forced username change without a new name (CO-BUG-12): your username
 *   is a stand-in (`user.xxxxxxxx`) until you choose one, so the app asks
 *   (`MustRenameGate`, the account-auth form shared with Settings › Profile),
 *   after any warning;
 * - Console v2 (§3.3): a warning's "Appeal", the answer to an appeal once
 *   staff decided it, and the Report and appeal dialogs themselves;
 * - Console v2 (§3.3): what staff wrote when they closed a case about you,
 *   in the same queue and dialog as a warning (`kind: 'case'`), with no
 *   category, nothing against your standing and nothing to appeal.
 */
import {
  USER_WARNINGS_PATH,
  type GatewayDispatch,
  type UserCaseNotice,
  type UserStaffNotice,
} from '@neosocial/protocol';
import { useState } from 'react';
import { AppealHost, AppealOutcome, loadAppealOutcomes, openAppeal } from '../appeals/AppealDialog';
import { ReportHost } from '../report/ReportDialog';
import { create } from 'zustand';
import { useT } from '../i18n';
import { api } from '../lib/api';
import { errorText } from '../mod/actions';
import { MustRenameGate } from '../settings/Profile';
import { useData } from '../store/data';
import { Btn } from '../ui/kit';
import { Dialog } from '../ui/overlay';

const useWarnings = create<{ queue: UserStaffNotice[] }>(() => ({ queue: [] }));

function addWarnings(list: UserStaffNotice[]): void {
  useWarnings.setState((s) => {
    const have = new Set(s.queue.map((w) => w.id));
    return { queue: [...s.queue, ...list.filter((w) => !have.has(w.id))] };
  });
}

/** The gateway glue: a new warning, and on READY the ones not acknowledged yet. */
export function handleStaffNoticeDispatch(frame: GatewayDispatch): void {
  if (frame.t === 'NX_WARNING_CREATE') addWarnings([frame.d as UserStaffNotice]);
  else if (frame.t === 'READY') {
    void loadWarnings();
    void loadAppealOutcomes();
  }
}

export async function loadWarnings(): Promise<void> {
  try {
    addWarnings(await api.client.get<UserStaffNotice[]>(USER_WARNINGS_PATH));
  } catch {
    /* shown at the next start instead */
  }
}

export function StaffNotices() {
  return (
    <>
      <Notice />
      <ReportHost />
      <AppealHost />
    </>
  );
}

function Notice() {
  const mustRename = useData((d) => d.me?.nx_must_rename === true);
  const warning = useWarnings((s) => s.queue[0]);
  if (warning) return <WarningDialog w={warning} />;
  if (mustRename) return <MustRenameGate />;
  return <AppealOutcome />;
}

function WarningDialog({ w }: { w: UserStaffNotice }) {
  const { t, fmt } = useT();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ack = async (): Promise<boolean> => {
    setBusy(true);
    setErr(null);
    try {
      await api.client.post<void>(`${USER_WARNINGS_PATH}/${w.id}/ack`, {});
      useWarnings.setState((s) => ({ queue: s.queue.filter((x) => x.id !== w.id) }));
      return true;
    } catch (e) {
      setErr(errorText(e));
      return false;
    } finally {
      setBusy(false);
    }
  };
  if (w.kind === 'case')
    return <CaseMessage n={w} busy={busy} err={err} onAck={() => void ack()} />;
  return (
    <Dialog
      title={t('cfg.warn.title')}
      icon="alert"
      tone="danger"
      dismissable={false}
      onClose={() => {}}
      cls="ns-warn-dlg"
      actions={
        <>
          {/* Console v2: ask staff to review it (read first, so it is acknowledged) */}
          <Btn
            kind="ghost"
            disabled={busy}
            onClick={async () => {
              if (await ack()) openAppeal({ type: 'warning', violationId: w.id });
            }}
          >
            {t('ap.appeal')}
          </Btn>
          <Btn kind="primary" busy={busy} onClick={() => void ack()}>
            {t('cfg.warn.ok')}
          </Btn>
        </>
      }
    >
      <p>
        {t('cfg.warn.body', {
          category: t(`cfg.warn.cat.${w.category}`),
          severity: t(`cfg.warn.sev.${w.severity}`),
        })}
      </p>
      {w.message ? (
        <blockquote className="ns-warn-msg" dir="auto">
          {w.message}
        </blockquote>
      ) : null}
      <p className="ns-help">
        {w.expires_at
          ? t('cfg.warn.until', { date: fmt.date(w.expires_at) })
          : t('cfg.warn.when', { date: fmt.date(w.created_at) })}
      </p>
      <AckError err={err} />
    </Dialog>
  );
}

/** A closed case's message: staff's words and "I understand" (not a warning: no Appeal). */
function CaseMessage({
  n,
  busy,
  err,
  onAck,
}: {
  n: UserCaseNotice;
  busy: boolean;
  err: string | null;
  onAck(): void;
}) {
  const { t, fmt } = useT();
  return (
    <Dialog
      title={t('cfg.case.title')}
      icon="shield"
      tone="mode"
      dismissable={false}
      onClose={() => {}}
      cls="ns-warn-dlg"
      actions={
        <Btn kind="primary" busy={busy} onClick={onAck}>
          {t('cfg.warn.ok')}
        </Btn>
      }
    >
      <p>{t('cfg.case.body')}</p>
      <blockquote className="ns-warn-msg" dir="auto">
        {n.message}
      </blockquote>
      <p className="ns-help">{t('cfg.case.when', { date: fmt.date(n.created_at) })}</p>
      <AckError err={err} />
    </Dialog>
  );
}

function AckError({ err }: { err: string | null }) {
  return err ? (
    <p className="ns-err" role="alert">
      {err}
    </p>
  ) : null;
}
